/**
 * Pure form logic for the delivery subscription editor (track B2).
 *
 * Split out of `DeliverySubscriptionsPanel.tsx` so it can be unit-tested
 * directly: the builder frontend's vitest runs in the `node`
 * environment with no jsdom and no @testing-library, so a rule that
 * matters — "you cannot submit a delivery without a credential
 * reference" — has to live somewhere importable without React.
 *
 * These checks MIRROR the backend and never replace it: the API
 * re-validates every field with croniter / zoneinfo / the adapter's own
 * `validate_target` and answers 422. Client-side validation is here to
 * save a round-trip on an obvious typo, not to be the gate.
 */

import type {
  DeliveryChannel,
  DeliverySubscription,
  DeliverySubscriptionInput,
} from "@/lib/builder-api";

export const CHANNELS: DeliveryChannel[] = [
  "webhook",
  "email",
  "slack",
  "teams",
];

/** Locales the snapshot pipeline can render/translate into. Mirrors
 * `delivery_channels.render.supported_locales()`. */
export const LOCALES = ["en", "es"];

/** Channels whose adapter declares `requires_secret`. Mirrors
 * `ChannelAdapter.requires_secret`; in phase 1 every channel needs one,
 * but the question is asked explicitly so a future credential-less
 * adapter is a one-line change. */
export const REQUIRES_SECRET: Record<DeliveryChannel, boolean> = {
  webhook: true,
  email: true,
  slack: true,
  teams: true,
};

/**
 * Teams target mode. NOT "incoming_webhook" any more.
 *
 * Microsoft retired Office 365 connectors inside Teams in the 18-22 May
 * 2026 window, so a classic `*.webhook.office.com` incoming webhook is
 * dead. Teams is now reached through the Workflows (Power Automate) app
 * and its "When a Teams webhook request is received" trigger, which
 * yields a plain HTTPS URL. Mirrors
 * `delivery_channels.adapters.teams.WORKFLOW_MODE`.
 */
export const TEAMS_WORKFLOW_MODE = "workflow";

/** Value stored by subscriptions created before the retirement. Still
 * accepted by the backend and normalised on load; never emitted. */
export const TEAMS_LEGACY_MODE = "incoming_webhook";

export function defaultModeFor(channel: DeliveryChannel): string {
  return channel === "teams" ? TEAMS_WORKFLOW_MODE : "incoming_webhook";
}

/**
 * Ref → env var the operator must set on the builder container.
 *
 * Mirrors `delivery_channels.secrets.env_var_name`, which is
 * deterministic precisely so a human can be told the exact variable
 * name. Shown in the Teams guide because until Vault write-through
 * lands (Plan C phase 2.2) the URL cannot be stored from this form —
 * saying so is better than a field that pretends otherwise.
 */
export function envVarNameFor(ref: string): string {
  const slug = ref.trim().replace(/[^A-Za-z0-9]/g, "_").toUpperCase();
  return `DELIVERY_SECRET__${slug}`;
}

export interface FormState {
  id: string | null;
  name: string;
  /**
   * The reusable channel credential this delivery authenticates with
   * (Plan C 2.3). Preferred over `secret_ref`: it points at a row that
   * can be rotated once for every schedule that shares it, and whose
   * value can actually be typed into the web.
   *
   * Empty string means "none picked". A subscription written before
   * migration 033 may still carry only a `secret_ref`; that keeps
   * working and is what `secretRef` validates.
   */
  credential_id: string;
  report_key: string;
  channel: DeliveryChannel;
  cron: string;
  timezone: string;
  locale: string;
  secret_ref: string;
  max_content_age_days: number;
  on_missing_content: "fail" | "trigger_watcher";
  enabled: boolean;
  /** Channel-specific target fields, flattened for the form. */
  url: string;
  recipients: string;
  subjectPrefix: string;
  mode: string;
  slackChannel: string;
}

export function emptyForm(reportKey: string, timezone = "UTC"): FormState {
  return {
    id: null,
    name: "",
    credential_id: "",
    report_key: reportKey,
    channel: "webhook",
    cron: "0 8 * * 1",
    timezone,
    locale: "en",
    secret_ref: "",
    max_content_age_days: 7,
    // 'fail' is the default on purpose: delivering stale insights as if
    // they were current is the failure mode this whole track avoids.
    on_missing_content: "fail",
    enabled: false,
    url: "",
    recipients: "",
    subjectPrefix: "",
    mode: "incoming_webhook",
    slackChannel: "",
  };
}

export function formFromSubscription(sub: DeliverySubscription): FormState {
  const target = sub.target ?? {};
  const recipients = Array.isArray(target.recipients)
    ? (target.recipients as string[]).join(", ")
    : "";
  return {
    id: sub.id,
    name: sub.name,
    credential_id: sub.credential_id ?? "",
    report_key: sub.report_key,
    channel: sub.channel,
    cron: sub.cron,
    timezone: sub.timezone,
    locale: sub.locale,
    secret_ref: sub.secret_ref ?? "",
    max_content_age_days: sub.max_content_age_days,
    on_missing_content: sub.on_missing_content,
    enabled: sub.enabled,
    url: typeof target.url === "string" ? target.url : "",
    recipients,
    subjectPrefix:
      typeof target.subject_prefix === "string" ? target.subject_prefix : "",
    // A Teams row stored before the connector retirement says
    // `incoming_webhook`. Show it as what it now is, so re-saving the
    // subscription writes the corrected value instead of carrying the
    // dead vocabulary forward.
    mode:
      sub.channel === "teams"
        ? TEAMS_WORKFLOW_MODE
        : typeof target.mode === "string"
          ? target.mode
          : "incoming_webhook",
    slackChannel: typeof target.channel === "string" ? target.channel : "",
  };
}

/**
 * Switching channel RESETS every target field and the secret ref.
 *
 * Carrying a webhook URL — or worse, a Slack secret reference — across
 * to an email subscription is how a delivery ends up pointing somewhere
 * nobody intended.
 */
export function withChannel(
  form: FormState,
  channel: DeliveryChannel,
): FormState {
  return {
    ...form,
    channel,
    url: "",
    recipients: "",
    subjectPrefix: "",
    mode: defaultModeFor(channel),
    slackChannel: "",
    secret_ref: "",
    // A credential belongs to ONE channel, so carrying it across would
    // aim a Slack schedule at the Teams URL. The backend refuses that
    // (422 naming both channels); resetting here means the operator
    // never has to read that error.
    credential_id: "",
  };
}

/** 5-field cron shape check. The backend parses it with croniter. */
export function isCronShapeValid(cron: string): boolean {
  return cron.trim().split(/\s+/).filter(Boolean).length === 5;
}

/** `tenants/<tenant>/delivery/<channel>/<name>`. Mirrors
 * `delivery_channels.secrets._REF_RE`. */
export function isSecretRefValid(ref: string): boolean {
  return /^tenants\/[a-z0-9-]+\/delivery\/(webhook|email|slack|teams)\/[a-z0-9._-]+$/.test(
    ref.trim(),
  );
}

export function parseRecipients(raw: string): string[] {
  return raw
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
}

export function buildTarget(form: FormState): Record<string, unknown> {
  switch (form.channel) {
    case "webhook":
      return { url: form.url.trim() };
    case "email":
      return {
        recipients: parseRecipients(form.recipients),
        ...(form.subjectPrefix.trim()
          ? { subject_prefix: form.subjectPrefix.trim() }
          : {}),
      };
    case "slack":
      return form.mode === "bot_token"
        ? { mode: "bot_token", channel: form.slackChannel.trim() }
        : { mode: "incoming_webhook" };
    case "teams":
      return { mode: TEAMS_WORKFLOW_MODE };
  }
}

/** Field keys that are invalid. Empty ⇒ the form may be submitted. */
export function formErrors(form: FormState): string[] {
  const errs: string[] = [];
  if (!form.name.trim()) errs.push("name");
  if (!form.report_key) errs.push("report");
  if (!isCronShapeValid(form.cron)) errs.push("cron");
  if (!form.timezone.trim()) errs.push("timezone");
  if (
    REQUIRES_SECRET[form.channel] &&
    form.credential_id.trim() === "" &&
    !isSecretRefValid(form.secret_ref)
  ) {
    // Either is enough. A credential is the way forward (rotatable,
    // reusable, storable from the web); a well-formed legacy ref still
    // validates so a pre-033 subscription can be re-saved untouched —
    // but since F0 the ref is never TYPED: the form only offers the
    // credential picker, and the ref rides along invisibly on rows
    // that already carry one.
    errs.push("credential");
  }
  if (form.channel === "webhook" && !form.url.trim().startsWith("https://")) {
    // http:// would put the customer's insights and the signature
    // header on the wire in plaintext.
    errs.push("url");
  }
  if (form.channel === "email" && parseRecipients(form.recipients).length === 0) {
    errs.push("recipients");
  }
  if (
    form.channel === "slack" &&
    form.mode === "bot_token" &&
    !form.slackChannel.trim()
  ) {
    errs.push("channel");
  }
  return errs;
}

/* ─── Schedule ↔ cron (Plan F, F0) ────────────────────────────────────
 *
 * The wizard's "Cuándo" step speaks daily/weekly/monthly + a time; the
 * stored field is still the same 5-field `cron` string the scheduler
 * has always read — nothing changes in the data model, only how the
 * string gets produced. A cron that does not fit one of the three
 * shapes round-trips as `custom` with the raw string PRESERVED and
 * shown, never silently rewritten.
 */

export type ScheduleKind = "daily" | "weekly" | "monthly" | "custom";

export interface ScheduleState {
  kind: ScheduleKind;
  /** "HH:MM", 24h — the shape `<input type="time">` speaks. */
  time: string;
  /** Cron day-of-week numbers, 0=Sunday … 6=Saturday. Weekly only. */
  weekdays: number[];
  /** 1..31. Monthly only. */
  monthDay: number;
  /** Authoritative only when kind === "custom". */
  rawCron: string;
}

/** UI display order: Monday-first, Sunday last (cron numbers). */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const INT_RE = /^\d{1,2}$/;
const DOW_LIST_RE = /^[0-7](,[0-7])*$/;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function isTimeShapeValid(time: string): boolean {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return false;
  return Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/**
 * Parse a stored cron into the wizard's structured shape.
 *
 * Recognised shapes (minute and hour must be plain integers):
 *   daily    `M H * * *`
 *   weekly   `M H * * d[,d…]`   (7 normalised to 0 = Sunday)
 *   monthly  `M H D * *`
 * Anything else — steps, ranges, names, non-* months — comes back as
 * `custom` carrying the raw string, which the UI shows as-is.
 */
export function scheduleFromCron(cron: string): ScheduleState {
  const custom: ScheduleState = {
    kind: "custom",
    time: "08:00",
    weekdays: [1],
    monthDay: 1,
    rawCron: cron,
  };
  const parts = cron.trim().split(/\s+/).filter(Boolean);
  if (parts.length !== 5) return custom;
  const [min, hour, dom, mon, dow] = parts;
  if (!INT_RE.test(min) || !INT_RE.test(hour) || mon !== "*") return custom;
  const m = Number(min);
  const h = Number(hour);
  if (m > 59 || h > 23) return custom;
  const time = `${pad2(h)}:${pad2(m)}`;
  if (dom === "*" && dow === "*") {
    return { kind: "daily", time, weekdays: [1], monthDay: 1, rawCron: cron };
  }
  if (dom === "*" && DOW_LIST_RE.test(dow)) {
    const days = [
      ...new Set(dow.split(",").map((d) => (Number(d) === 7 ? 0 : Number(d)))),
    ].sort((a, b) => a - b);
    return { kind: "weekly", time, weekdays: days, monthDay: 1, rawCron: cron };
  }
  if (
    dow === "*" &&
    INT_RE.test(dom) &&
    Number(dom) >= 1 &&
    Number(dom) <= 31
  ) {
    return {
      kind: "monthly",
      time,
      weekdays: [1],
      monthDay: Number(dom),
      rawCron: cron,
    };
  }
  return custom;
}

/**
 * Serialise the structured schedule back to the stored cron string.
 *
 * A weekly schedule with no weekdays produces a 4-field string on
 * purpose: it fails `isCronShapeValid` (and the backend's croniter)
 * instead of inventing a day nobody picked.
 */
export function cronFromSchedule(s: ScheduleState): string {
  if (s.kind === "custom") return s.rawCron.trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.time.trim());
  if (!m) return "";
  const minute = Number(m[2]);
  const hour = Number(m[1]);
  switch (s.kind) {
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      return `${minute} ${hour} * * ${[...new Set(s.weekdays)]
        .sort((a, b) => a - b)
        .join(",")}`;
    case "monthly":
      return `${minute} ${hour} ${s.monthDay} * *`;
  }
}

/** Schedule-level errors the cron string alone cannot name. */
export function scheduleErrors(s: ScheduleState): string[] {
  const errs: string[] = [];
  if (s.kind === "custom") {
    if (!isCronShapeValid(s.rawCron)) errs.push("cron");
    return errs;
  }
  if (!isTimeShapeValid(s.time)) errs.push("time");
  if (s.kind === "weekly" && s.weekdays.length === 0) errs.push("weekdays");
  if (s.kind === "monthly" && (s.monthDay < 1 || s.monthDay > 31)) {
    errs.push("monthDay");
  }
  return errs;
}

/**
 * Curated IANA zones with human names, ES/EN. The wizard shows these in
 * a select — nobody types `America/Santiago` by hand — and injects the
 * browser zone (or a stored exotic one) as an extra option when it is
 * not in the list, labelled by its raw id rather than dropped.
 */
export const TIMEZONES: { id: string; label: { en: string; es: string; pt: string } }[] = [
  {
    id: "America/Santiago",
    label: { en: "Chile — Santiago", es: "Chile — Santiago", pt: "Chile, Santiago" },
  },
  {
    id: "America/Mexico_City",
    label: { en: "Mexico — Mexico City", es: "México — Ciudad de México", pt: "México, Cidade do México" },
  },
  {
    id: "America/Bogota",
    label: { en: "Colombia — Bogotá", es: "Colombia — Bogotá", pt: "Colômbia, Bogotá" },
  },
  { id: "America/Lima", label: { en: "Peru — Lima", es: "Perú — Lima", pt: "Peru, Lima" } },
  {
    id: "America/Argentina/Buenos_Aires",
    label: {
      en: "Argentina — Buenos Aires",
      es: "Argentina — Buenos Aires", pt: "Argentina, Buenos Aires",
    },
  },
  {
    id: "America/Sao_Paulo",
    label: { en: "Brazil — São Paulo", es: "Brasil — São Paulo", pt: "Brasil, São Paulo" },
  },
  {
    id: "America/New_York",
    label: {
      en: "US — Eastern (New York)",
      es: "EE.UU. — Este (Nueva York)", pt: "EUA, Leste (Nova York)",
    },
  },
  {
    id: "America/Chicago",
    label: { en: "US — Central (Chicago)", es: "EE.UU. — Centro (Chicago)", pt: "EUA, Centro (Chicago)" },
  },
  {
    id: "America/Denver",
    label: {
      en: "US — Mountain (Denver)",
      es: "EE.UU. — Montaña (Denver)", pt: "EUA, Montanha (Denver)",
    },
  },
  {
    id: "America/Los_Angeles",
    label: {
      en: "US — Pacific (Los Angeles)",
      es: "EE.UU. — Pacífico (Los Ángeles)", pt: "EUA, Pacífico (Los Angeles)",
    },
  },
  { id: "Europe/Madrid", label: { en: "Spain — Madrid", es: "España — Madrid", pt: "Espanha, Madri" } },
  {
    id: "Europe/London",
    label: { en: "UK — London", es: "Reino Unido — Londres", pt: "Reino Unido, Londres" },
  },
  { id: "UTC", label: { en: "UTC", es: "UTC", pt: "UTC" } },
];

export function timezoneLabel(id: string, locale: string): string {
  const found = TIMEZONES.find((z) => z.id === id);
  if (!found) return id;
  // Same narrowing as dashboard-render's ``toLang`` (an unknown tag reads
  // as English); kept local so this pure module stays React-free.
  const lang = (["es", "pt"] as const).find((l) => locale.startsWith(l));
  return found.label[lang ?? "en"];
}

/** The zone the admin's browser reports — the wizard's default for a
 * NEW subscription. Existing rows keep their stored zone untouched. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function toInput(form: FormState): DeliverySubscriptionInput {
  return {
    name: form.name.trim(),
    report_key: form.report_key,
    channel: form.channel,
    target: buildTarget(form),
    credential_id: form.credential_id.trim(),
    secret_ref: form.secret_ref.trim(),
    cron: form.cron.trim(),
    timezone: form.timezone.trim(),
    locale: form.locale,
    max_content_age_days: form.max_content_age_days,
    on_missing_content: form.on_missing_content,
    options: {},
    enabled: form.enabled,
  };
}
